import { Link, useSearch } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import type { ReportSummary } from "@/lib/types";
import { formatCents, formatDate, formatInt, formatPercent } from "@/lib/format";
import { cn } from "@/lib/utils";
import { BrandMark } from "@/components/BrandMark";
import { Empty, PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { KIND_LABEL, useCompare } from "./reportHooks";

type Metric = {
  label: string;
  value: (s: ReportSummary) => number;
  show: (v: number) => string;
  better: "high" | "low" | null;
  help?: string;
};

const METRICS: Metric[] = [
  { label: "Sent", value: (s) => s.sent, show: formatInt, better: null },
  { label: "Delivered", value: (s) => (s.sent ? s.delivered / s.sent : 0), show: (v) => formatPercent(v), better: "high" },
  { label: "Opened by a person", value: (s) => s.human_open_rate, show: (v) => formatPercent(v), better: "high", help: "Machine opens left out" },
  { label: "Opened, incl. machines", value: (s) => s.open_rate, show: (v) => formatPercent(v), better: "high" },
  { label: "Clicked", value: (s) => s.click_rate, show: (v) => formatPercent(v), better: "high" },
  { label: "Click-to-open", value: (s) => s.click_to_open_rate, show: (v) => formatPercent(v), better: "high", help: "Clickers as a share of openers" },
  { label: "Unsubscribed", value: (s) => s.unsubscribe_rate, show: (v) => formatPercent(v, 2), better: "low" },
  { label: "Bounced", value: (s) => s.bounce_rate, show: (v) => formatPercent(v, 2), better: "low" },
  { label: "Spam reports", value: (s) => s.complaint_rate, show: (v) => formatPercent(v, 3), better: "low" },
  { label: "Orders", value: (s) => s.orders, show: formatInt, better: "high" },
  { label: "Revenue", value: (s) => s.revenue_cents, show: (v) => formatCents(v), better: "high" },
  { label: "Revenue per 1,000 sent", value: (s) => (s.sent ? (s.revenue_cents / s.sent) * 1000 : 0), show: (v) => formatCents(Math.round(v)), better: "high" },
];

/** Side by side: 2 to 10 sends, every rate in one row, the best one in each row marked. */
export function ComparePage() {
  const { ids = [] } = useSearch({ from: "/email/reports/compare" });
  const q = useCompare(ids);
  const reports = q.data?.reports ?? [];
  return (
    <div className="space-y-3">
      <Link to="/email/reports" className="inline-flex items-center gap-1 text-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]">
        <ArrowLeft className="size-4" /> Email reports
      </Link>
      <PageHeader title="Compare emails" sub={reports.length ? `${reports.length} side by side` : undefined} />
      {ids.length < 2 && (
        <Card>
          <Empty title="Pick 2 to 10 emails to compare.">Tick the boxes next to them on the reports list, then press Compare.</Empty>
        </Card>
      )}
      {q.isLoading && <Skeleton className="h-80" />}
      {reports.length >= 2 && (
        <Card className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b align-top">
                <th className="sticky left-0 bg-[var(--color-card)] px-3 py-2 text-left text-xs font-medium text-[var(--color-muted-foreground)]">Email</th>
                {reports.map((r) => (
                  <th key={r.report_unit_id} className="min-w-40 px-3 py-2 text-left font-normal">
                    <Link to="/email/reports/$reportId" params={{ reportId: String(r.report_unit_id) }} className="font-medium text-[var(--color-primary)] hover:underline">
                      {r.name}
                    </Link>
                    <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-[var(--color-muted-foreground)]">
                      {r.brand_id && <BrandMark id={r.brand_id} />}
                      <span>{formatDate(r.first_sent_at)}</span>
                      <span>{KIND_LABEL[r.kind] ?? r.kind}</span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {METRICS.map((m) => {
                const vals = reports.map(m.value);
                const max = Math.max(...vals, 0);
                const best = m.better === "high" ? max : m.better === "low" ? Math.min(...vals) : null;
                const distinct = new Set(vals).size > 1;
                return (
                  <tr key={m.label} className="border-b last:border-0">
                    <th scope="row" className="sticky left-0 whitespace-nowrap bg-[var(--color-card)] px-3 py-2 text-left text-xs font-medium text-[var(--color-muted-foreground)]" title={m.help}>
                      {m.label}
                    </th>
                    {vals.map((v, i) => {
                      const isBest = best !== null && distinct && v === best;
                      return (
                        <td key={reports[i].report_unit_id} className="px-3 py-2">
                          <div className={cn("num", isBest && "font-semibold")}>
                            {m.show(v)}
                            {isBest && <span className="ml-1.5 text-xs font-normal text-[var(--color-success)]">best</span>}
                          </div>
                          {m.better !== null && max > 0 && (
                            <div className="mt-1 h-1 rounded-full bg-[var(--color-muted)]" aria-hidden>
                              <div className="h-full rounded-full bg-[var(--color-chart-opens)]" style={{ width: `${(v / max) * 100}%` }} />
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
